import { useState } from 'react';
import Stepper, { Step } from './Stepper';
import interviewImage from '@/assets/interview-screenshot-new.png';
import interviewImageMobile from '@/assets/interview-screenshot-mobile.png';
import evaluationImage from '@/assets/evaluation-screenshot.png';
import evaluationImageMobile from '@/assets/evaluation-screenshot-mobile.png';
import feedbackImage from '@/assets/feedback-screenshot.png';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';

const emailSchema = z.string().trim().email({
  message: "Inserisci un'email valida"
}).max(255);

const MainFeatures = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      emailSchema.parse(email);
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from('lead_companies')
        .insert([{ email }]);

      if (error) throw error;

      toast({
        title: "Grazie per il tuo interesse!",
        description: "Ti contatteremo presto per mostrarti la piattaforma.",
      });
      
      setEmail('');
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Errore",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Errore",
          description: "Si è verificato un errore. Riprova più tardi.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 px-6 bg-[#f8faff]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            Scopri le funzionalità principali
          </h2>
          <p className="text-xl text-secondary max-w-3xl mx-auto">
            Tutto ciò che ti serve per individuare, attrarre, e fidelizzare talenti.
          </p>
        </div>
        
        <Stepper
          initialStep={1}
          onStepChange={(step) => {
            console.log('Step corrente:', step);
          }}
          onFinalStepCompleted={() => console.log('Tutti gli step completati!')}
          backButtonText="Indietro"
          nextButtonText="Avanti"
        >
          <Step>
            <div className="mb-3 md:mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-primary">Esegui colloqui automatici per tutti i candidati</h2>
            </div>
            {/* Immagine mobile */}
            <img 
              src={interviewImageMobile} 
              alt="Interfaccia colloquio automatico" 
              className="block md:hidden w-full max-w-xs mx-auto h-64 rounded-lg object-cover" 
            />
            {/* Immagine desktop */}
            <img 
              src={interviewImage} 
              alt="Interfaccia colloquio automatico" 
              className="hidden md:block w-full h-96 rounded-lg object-contain" 
            />
          </Step>

          <Step>
            <div className="mb-3 md:mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-primary">Ricevi le valutazioni del CV e del colloquio: scegli consapevolmente</h2>
            </div>
            {/* Immagine mobile */}
            <img 
              src={evaluationImageMobile} 
              alt="Valutazioni CV e colloquio" 
              className="block md:hidden w-[95%] max-w-[320px] mx-auto h-64 rounded-lg object-cover" 
            />
            {/* Immagine desktop */}
            <img 
              src={evaluationImage} 
              alt="Valutazioni CV e colloquio" 
              className="hidden md:block w-full h-96 rounded-lg object-contain" 
            />
          </Step>

          <Step>
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-primary">Produci feedback automatici per i tuoi candidati</h2>
              
              <div className="space-y-2 md:space-y-4 mt-6 md:mt-8">
                <div className="flex items-start gap-2 md:gap-3 p-2 md:p-4 bg-background/50 rounded-lg border border-border/50">
                  <div className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold text-sm md:text-base">1</span>
                  </div>
                  <p className="text-secondary text-sm md:text-lg">Perché non sei stato selezionata</p>
                </div>
                
                <div className="flex items-start gap-2 md:gap-3 p-2 md:p-4 bg-background/50 rounded-lg border border-border/50">
                  <div className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold text-sm md:text-base">2</span>
                  </div>
                  <p className="text-secondary text-sm md:text-lg">Cosa emerge dal CV e dai colloqui</p>
                </div>
                
                <div className="flex items-start gap-2 md:gap-3 p-2 md:p-4 bg-background/50 rounded-lg border border-border/50">
                  <div className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold text-sm md:text-base">3</span>
                  </div>
                  <p className="text-secondary text-sm md:text-lg">Cosa puoi fare per migliorarti</p>
                </div>
                
                <div className="flex items-start gap-2 md:gap-3 p-2 md:p-4 bg-background/50 rounded-lg border border-border/50">
                  <div className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold text-sm md:text-base">4</span>
                  </div>
                  <p className="text-secondary text-sm md:text-lg">Come ti posizioni sul mercato</p>
                </div>
              </div>
            </div>
          </Step>

          <Step>
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-primary mb-4">Pronto a rivoluzionare il tuo recruiting?</h2>
              </div>
              
              <div className="bg-background p-4 md:p-6 rounded-lg border border-border shadow-sm">
                <h3 className="text-base md:text-lg font-semibold text-primary mb-2 md:mb-3">
                  Richiedi una demo gratuita
                </h3>
                <p className="text-muted-foreground mb-3 md:mb-4 text-sm">
                  Lascia la tua email e ti contatteremo per mostrarti la piattaforma in azione
                </p>
                
                <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-2">
                  <Input
                    type="email"
                    placeholder="la-tua-email@azienda.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1"
                    required
                  />
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="rounded-full px-6 text-gray-50 w-full md:w-auto"
                  >
                    {isSubmitting ? 'Invio...' : 'Richiedi Demo'}
                  </Button>
                </form>
              </div>
            </div>
          </Step>
        </Stepper>
      </div>
    </section>
  );
};

export default MainFeatures;